import type { ApiResponse, ApiResponseRecord } from '../models/ApiResponse';
import type { TableData } from '../models/TableData';

/**
 * Normalizes an epoch timestamp to milliseconds. Auto-detects whether the input
 * is in seconds (10 digits, typical for DynamoDB TTL) or milliseconds (13 digits,
 * typical for JavaScript Date) and returns milliseconds in either case.
 *
 * Threshold: any value > 9,999,999,999 (~Nov 2286 in seconds) is treated as
 * already being in milliseconds; values at or below are treated as seconds.
 *
 * @param {number} timestamp - An epoch timestamp in either seconds or milliseconds.
 * @return {number} The timestamp normalized to epoch milliseconds.
 */
export function toMillis(timestamp: number): number {
  return timestamp > 9_999_999_999 ? timestamp : timestamp * 1000;
}

/**
 * Transforms an API response containing records into a structured array of TableData objects.
 * This function is useful for converting the raw API data into a format suitable for displaying
 * in the metadata results table.
 *
 * @param {ApiResponse} apiResponse - The API response containing the records to be transformed.
 * @return {TableData[]} An array of TableData objects, each representing a row for display in a table.
 */
export const getTableDataFromApiResponse = function(apiResponse: ApiResponse): TableData[] {
  // if there are no records in the response, return an empty array
  if(!apiResponse || !apiResponse.records) {
    return []
  }

  return getTableDataFromApiResponseRecords(apiResponse.records);
}

export const getTableDataFromApiResponseRecords = function(responseRecords: ApiResponseRecord[]): TableData[] {
  // if there are no records in the response, return an empty array
  if(!responseRecords || responseRecords.length === 0) {
    return []
  }

  // Sort records by uploadTimestamp descending (most recent first).
  // Normalize via toMillis() so epoch-seconds and epoch-milliseconds
  // records are compared on the same scale.
  const sortedRecords = [...responseRecords].sort((a, b) =>
    toMillis(b.uploadTimestamp ?? 0) - toMillis(a.uploadTimestamp ?? 0)
  );

  const tableData: TableData[] = []

  sortedRecords.forEach((record) => {
    const tableDataRow: TableData = {
      ccode: record.ccode,
      correlationId: record.correlationId,
      dateUploaded: '',
      downloadLink: '',
      eligibleAddresses: String(record.eligibleAddresses),
      uploadFileName: record.uploadFileName,
      status: record.status,
      totalAddresses: String(record.totalAddresses),
      uploadedBy: '',
      userID: record.userId
    }

    // Determining the latest upload date
    if(record.uploadTimestamp) {
      tableDataRow.dateUploaded = new Date(toMillis(record.uploadTimestamp)).toLocaleString()
    }
    else {
      tableDataRow.dateUploaded = ''
    }

    // Creating the uploadedBy name from the user's first and last name
    record.firstName && (
      tableDataRow.uploadedBy = record.firstName + ' '
    )
    record.lastName && (
      tableDataRow.uploadedBy += record.lastName
    )

    // TODO:  add something for the context for the download link
    // tableDataRow.downloadLink = record.downloadLink;

    tableData.push(tableDataRow)
  })

  return tableData
}
